import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography, Button, LinearProgress } from '@mui/material';
import { AlertOctagon, ShieldOff, X } from 'lucide-react';
import styled from '@emotion/styled';

const ModalOverlay = styled(motion.div)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(8px)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
});

const ModalContent = styled(motion.div)({
  background: 'white',
  borderRadius: '24px',
  maxWidth: '500px',
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.18)',
});

const StepIndicator = styled(Box)(({ active, completed }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  fontWeight: 600,
  background: completed ? '#FF6C6C' : active ? '#fff' : '#f3f4f6',
  color: completed ? '#fff' : active ? '#FF6C6C' : '#9ca3af',
  border: active ? '2px solid #FF6C6C' : 'none',
  transition: 'all 0.3s ease',
}));

const DeactivateModal = ({ isOpen, onClose, userDetails, statusChangeModalOpen, statusChangeType, onStatusChangeComplete }) => {
  const steps = [
    { title: 'Confirm Action', description: 'Verify deactivation request' },
    { title: 'Process Request', description: 'Update brand status' },
    { title: 'Notify Users', description: 'Send notifications' },
    { title: 'Complete', description: 'Finalize changes' },
  ];

  const currentStep = 2; // This would be managed by state in a real implementation

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
          >
            <Box sx={{ p: 4 }}>
              <Box sx={{ position: 'absolute', right: 16, top: 16 }}>
                <Button
                  onClick={onClose}
                  sx={{ minWidth: 'auto', p: 1, color: 'gray' }}
                >
                  <X size={20} />
                </Button>
              </Box>

              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 20
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: '#FF6C6C20',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 3
                    }}
                  >
                    <AlertOctagon size={40} color="#FF6C6C" />
                  </Box>
                </motion.div>

                <Typography variant="h5" sx={{ color: '#FF6C6C', fontWeight: 600, mb: 1 }}>
                  Deactivate Brand Account
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 4 }}>
                  You are about to deactivate <strong>{userDetails?.brandName}</strong>
                </Typography>

                <Box sx={{ mb: 4 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={(currentStep / steps.length) * 100}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: '#f3f4f6',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#FF6C6C',
                      }
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                  {steps.map((step, index) => (
                    <Box
                      key={step.title}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'relative',
                        flex: 1,
                      }}
                    >
                      <StepIndicator
                        active={index === currentStep}
                        completed={index < currentStep}
                      >
                        {index + 1}
                      </StepIndicator>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          mt: 1,
                          color: index <= currentStep ? '#FF6C6C' : '#9ca3af',
                        }}
                      >
                        {step.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          mt: 0.5,
                        }}
                      >
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                      borderRadius: '12px',
                      px: 4,
                      borderColor: '#e5e7eb',
                      color: 'text.primary',
                      '&:hover': {
                        borderColor: '#d1d5db',
                        bgcolor: '#f9fafb'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={onStatusChangeComplete}
                    startIcon={<ShieldOff size={16} />}
                    sx={{
                      borderRadius: '12px',
                      px: 4,
                      bgcolor: '#FF6C6C',
                      '&:hover': { bgcolor: '#ff8f8f' }
                    }}
                  >
                    Confirm Deactivation
                  </Button>
                </Box>
              </Box>
            </Box>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default DeactivateModal;
